import { AppButton, AppTextField } from 'components';
import { useEffect, useState } from 'react';
import Joi from 'joi';
import { checkForErrors } from 'utilities/fns';
import AppColourPicker from 'components/ColourPicker/ColourPicker';
import AppImagePicker from 'components/ImagePicker';
import { DEFAULT_COLOURS } from 'utilities/constants';

function ManageSubjects({ save, selectedItem = {}, handleSelectedItem, isPageMode = false }) {
  const [subjectName, handleSubjectName] = useState('');
  const [key, handleKey] = useState('');
  const [colour, handleColour] = useState(Object.assign({}, DEFAULT_COLOURS));
  const [imageData, handleImageData] = useState(null);
  const [disableSubmit, handleDisabling] = useState(false);
  const [formErrors, setFormErrors] = useState([]);

  useEffect(() => {
    if (selectedItem?._id) {
      handleSubjectName(selectedItem?.name ?? '');
      handleKey(selectedItem?.key ?? '');
      if (selectedItem?.color && selectedItem?.color?.primary && selectedItem?.color?.secondary) {
        handleColour(selectedItem?.color);
      } else {
        handleColour(Object.assign({}, DEFAULT_COLOURS));
      }
      handleImageData({ imageUrl: selectedItem?.iconFullUrl, logoUrl: selectedItem?.icon });
    }
  }, [selectedItem]);

  function handleSave() {
    if (disableSubmit) return;

    setFormErrors([]);

    const data = {
      name: `${subjectName}`.trim(),
      key: `${key}`.trim(),
      color: colour,
      logoUrl: imageData?.logoUrl,
      logoURL: imageData?.logoUrl,
      icon: imageData?.logoUrl,
    };

    const validation = manageSubjectsSchema.validate(data, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    save?.({ ...data, id: selectedItem?._id, status: selectedItem?._id ? 'active' : undefined });

    clearData();
  }

  function clearData() {
    setFormErrors([]);

    handleSubjectName('');
    handleImageData(null);
    handleKey('');
    handleColour(Object.assign({}, DEFAULT_COLOURS));
    handleSelectedItem({});
  }

  const errors = checkForErrors(formErrors);

  return (
    <>
      {!isPageMode && (
        <div className="row">
          <div className="col-6">
            <h2 className="prime-head p-0 boldtitle">Manage subjects</h2>
            {selectedItem?._id ? (
              <span>Update the selected subject</span>
            ) : (
              <span>Create a new subject</span>
            )}
          </div>
          <div className="col-6 prime-head text-right"></div>
        </div>
      )}

      {isPageMode && (
        <div className="row">
          <div className="col-12">
            <h2 className="prime-head p-0 boldtitle">
              {selectedItem?._id ? 'Update Subject' : 'Add Subject'}
            </h2>
          </div>
        </div>
      )}

      <div className="row mt-4">
        <div className="col-lg-4 col-md-6 col-sm-12">
          <div className="col-lg-12 col-md-6 col-sm-12">
            <AppTextField
              labelProps={{
                label: 'Subject name *',
                htmlFor: 'subjectName',
              }}
              inputProps={{
                placeholder: 'Subject name *',
                id: 'subjectName',
                name: 'subjectName',
                value: subjectName,
                onChange: (event) => handleSubjectName(event.target.value),
              }}
              fieldErrors={errors['name']}
            />
          </div>
          <div className="col-lg-12 col-md-6 col-sm-12 mt-4">
            <AppTextField
              labelProps={{
                label: 'Subject unique key',
                htmlFor: 'subjectUniqueKey',
              }}
              inputProps={{
                placeholder: 'Subject unique key',
                id: 'subjectUniqueKey',
                name: 'subjectUniqueKey',
                value: key,
                onChange: (event) => handleKey(event.target.value),
              }}
              fieldErrors={errors['key']}
            />
          </div>
        </div>
        <div className="col-lg-8 col-md-6 col-sm-12">
          <div className="col-lg-4 col-md-6 col-sm-12 disinline imgupload">
            <AppImagePicker
              image={imageData?.imageUrl}
              handleChange={(imageData) => handleImageData(imageData)}
              handleDisabling={(doDisable) => handleDisabling(doDisable)}
            />
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12 disinline colorpick">
            <label>Primary color</label>
            <AppColourPicker
              defaultHex={colour.primary}
              onChange={(primary) => handleColour((x) => ({ ...x, primary }))}
            />
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12 disinline colorpick">
            <label>Secondary color</label>
            <AppColourPicker
              defaultHex={colour.secondary}
              onChange={(secondary) => handleColour((x) => ({ ...x, secondary }))}
            />
          </div>
        </div>
      </div>

      <div className="d-flex flex-row mt-4 justify-content-end">
        <div className="aligncenter savecancelbtnsec">
          <AppButton
            variantClass="btn-primary mx-3"
            style={{ minWidth: '1.5rem' }}
            onClick={handleSave}
          >
            {selectedItem?._id ? 'Update Subject' : 'Save Subject'}
          </AppButton>
          {!isPageMode && (
            <AppButton variantClass="btn-plane p--tint mx-3" onClick={clearData}>
              Cancel
            </AppButton>
          )}
        </div>
      </div>
    </>
  );
}

const manageSubjectsSchema = Joi.object({
  name: Joi.string().required().trim(),
});

ManageSubjects.defaultProps = {
  save: () => {},
  handleSelectedItem: () => {},
};

export default ManageSubjects;
